import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AdditionalServiceBadge } from "@features/AdditionalServices/components/AdditionalServiceBadge/AdditionalServiceBadge";
import { ChevronIcon } from "@components/icons";
import { type IAdditionalService } from "@services/additional-services/types";
import { useOverviewQuery } from "@services/overview/queries";
import { formatImageSrc } from "@utils/common";
import { AdditionalServiceItemTitle } from "../AdditionalServiceItemTitle/AdditionalServiceItemTitle";
import { AdditionalServicePrice } from "../AdditionalServicePrice/AdditionalServicePrice";

interface IAdditionalServiceItemProps {
  service: IAdditionalService;
  isOrderedPage?: boolean;
  returnPath?: string;
}

export const AdditionalServiceItem: React.FC<IAdditionalServiceItemProps> = ({
  service,
  isOrderedPage,
  returnPath,
}) => {
  const { t } = useTranslation();
  const { data: overview } = useOverviewQuery();

  const { jointBillServices } = overview ?? { jointBillServices: [] };

  const alreadyOrderedJbs = jointBillServices.filter((jbs) =>
    (service?.orderedForJointBillServices ?? []).includes(
      jbs.jointBillServiceId,
    ),
  );

  const isServiceOrdered = !!service.orderedForJointBillServices?.length;

  const badge = isServiceOrdered ? (
    <AdditionalServiceBadge>
      {t("additionalServices.listItem.badges.ordered")}
    </AdditionalServiceBadge>
  ) : service.isNew ? (
    <AdditionalServiceBadge color="green">
      {t("additionalServices.listItem.badges.new")}
    </AdditionalServiceBadge>
  ) : null;

  return (
    <Link
      to={`/services/${service.additionalServiceId}${
        returnPath ? `?returnPath=${returnPath}` : ""
      }`}
      className="group w-full cursor-pointer overflow-hidden rounded-xl border border-[#DEDEDE] bg-base-white shadow-sm"
    >
      <div className="relative h-[191px] w-full border-b border-[#DEDEDE] p-0">
        {service.logo && (
          <img
            className="z-2 h-full w-full object-contain"
            src={formatImageSrc(service.logo)}
            alt={service.name}
          />
        )}
        <div className="z-3 absolute right-0 top-0 flex flex-row justify-between p-6">
          <div className="flex w-full flex-row justify-between">{badge}</div>
        </div>
      </div>
      {isOrderedPage ? (
        <div className="flex flex-col items-center justify-between">
          <div className="flex h-[calc(100%-191px)] w-full flex-row items-center justify-between border-b border-[#DEDEDE] p-6">
            <div className="flex h-full flex-col justify-between">
              <AdditionalServiceItemTitle title={service.name} />
              <AdditionalServicePrice service={service} />
            </div>
            <div className="flex w-14 justify-end">
              <ChevronIcon className="h-5 w-5 flex-shrink-0 -rotate-90 text-grey-dark group-hover:text-[#000000]" />
            </div>
          </div>
          <div className="flex w-full flex-col gap-3 p-6 text-sm">
            {!!alreadyOrderedJbs.length &&
              alreadyOrderedJbs.map((jbs) => (
                <div className="flex flex-row justify-between gap-4">
                  <span className="text-grey-dark">
                    {t("additionalServices.listItem.orderedFor")}
                  </span>
                  <span
                    className="truncate"
                    title={jbs.displayName ?? t("common.defaultAddress")}
                  >
                    {jbs.displayName ?? t("common.defaultAddress")}
                  </span>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="flex h-[calc(100%-191px)] flex-row items-center justify-between p-6">
          <div className="flex h-full flex-col justify-between gap-2">
            <AdditionalServiceItemTitle title={service.name} />
            <AdditionalServicePrice service={service} />
          </div>
          <div className="flex w-14 justify-end">
            <ChevronIcon className="h-5 w-5 flex-shrink-0 -rotate-90 text-grey-dark group-hover:text-base-black" />
          </div>
        </div>
      )}
    </Link>
  );
};
